import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Menu, QrCode, NfcIcon, BarChart3, Users, Clock, Shield, Download, Smartphone, Share, PlusSquare, Package, Mail, Phone, Linkedin, Instagram, Youtube, MessageCircle, Globe, MapPin } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import madelmoldLogo from "@/assets/madelmold-logo.png";
import tryLogo from "@/assets/try-logo.png";
import { ThemeToggle } from "@/components/ThemeToggle";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { APP_CONFIG } from "@/lib/config";

export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [showAndroidDialog, setShowAndroidDialog] = useState(false);
  const { isInstallable, isInstalled, isIOS, isAndroid, isMobile, canPrompt, promptInstall } = usePWAInstall();

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSDialog(true);
    } else if (canPrompt) {
      // HTTPS disponible - usar prompt nativo
      await promptInstall();
    } else if (isAndroid) {
      // HTTP - mostrar instrucciones manuales para Android
      setShowAndroidDialog(true);
    }
  };

  const features = [
    {
      icon: QrCode,
      title: "Trazabilidad QR",
      description: "Seguimiento completo de cada pieza en el proceso productivo"
    },
    {
      icon: NfcIcon,
      title: "Tecnología NFC",
      description: "Captura de datos en tiempo real con etiquetas inteligentes"
    },
    {
      icon: BarChart3,
      title: "Analytics Avanzado",
      description: "Métricas y KPIs personalizables para tomar mejores decisiones"
    },
    {
      icon: Users,
      title: "Gestión de Operadores",
      description: "Asignación inteligente y seguimiento de tareas"
    },
    {
      icon: Clock,
      title: "Tiempo Real",
      description: "Monitoreo instantáneo del estado de producción"
    },
    {
      icon: Shield,
      title: "Seguridad",
      description: "Control de acceso por roles y auditoría completa"
    }
  ];

  const stats = [
    { value: "99.9%", label: "Uptime" },
    { value: "24/7", label: "Soporte" },
    { value: "100%", label: "Trazabilidad" }
  ];

  return (
    <div className="min-h-screen bg-gradient-magma">
      {/* Header */}
      <header className="glass-card m-4 sm:m-6">
        <nav className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={madelmoldLogo} 
                alt="Manufacturas Madelmold S.A." 
                className="h-12 sm:h-14 w-auto object-contain"
              />
              <span className="text-lg sm:text-xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                Madelmold MES
              </span>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <ThemeToggle />
              {(isInstallable || isMobile) && !isInstalled && (
                <Button
                  variant="outline"
                  onClick={handleInstallClick}
                  className="border-primary/30 hover:bg-primary/10 font-medium gap-2"
                >
                  <Download className="w-4 h-4" />
                  Instalar App
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => navigate("/auth")}
                className="hover:bg-primary/10 font-medium"
              >
                Iniciar Sesión
              </Button>
              <Button
                onClick={() => navigate("/auth")}
                className="bg-gradient-magma-light text-white hover:shadow-magma-hover transition-all font-semibold"
              >
                Comenzar
              </Button>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </Button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-64 bg-card border-border">
          <div className="flex flex-col gap-4 mt-8">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
              <span className="text-sm font-medium text-foreground">Tema</span>
              <ThemeToggle />
            </div>
            {(isInstallable || isMobile) && !isInstalled && (
              <Button
                variant="outline"
                onClick={() => {
                  handleInstallClick();
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 border-primary/30 hover:bg-primary/10 font-medium"
              >
                <Download className="w-4 h-4" />
                Instalar App
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                navigate("/auth");
                setMobileMenuOpen(false);
              }}
              className="w-full justify-start hover:bg-primary/10 font-medium"
            >
              Iniciar Sesión
            </Button>
            <Button
              onClick={() => {
                navigate("/auth");
                setMobileMenuOpen(false);
              }}
              className="w-full bg-gradient-magma-light text-white hover:shadow-magma-hover font-semibold"
            >
              Comenzar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="glass-card max-w-4xl mx-auto text-center p-8 sm:p-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-magma bg-clip-text text-transparent">
            Sistema MES de Última Generación
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            Controla y optimiza tu producción con tecnología NFC y trazabilidad QR. 
            Perfecto para manufactura de composites y procesos industriales avanzados.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate("/auth")}
              className="w-full sm:w-auto bg-gradient-magma-light text-white hover:shadow-magma-hover transition-all text-base sm:text-lg px-8 py-6 rounded-full font-semibold"
            >
              Comenzar Ahora
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/auth")}
              className="w-full sm:w-auto text-base sm:text-lg px-8 py-6 rounded-full border-primary/30 hover:bg-primary/10 font-semibold"
            >
              Iniciar Sesión
            </Button>
          </div>

          {/* Descarga APK Android - Siempre visible */}
          <div className="mt-8 p-5 rounded-2xl bg-gradient-to-r from-orange-500/15 to-red-500/10 border-2 border-orange-500/30">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-foreground text-lg">📦 App Android Nativa</p>
                  <p className="text-sm text-muted-foreground">Versión {APP_CONFIG.version} • Soporte NFC completo</p>
                </div>
              </div>
              <Button
                asChild
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold gap-2 shadow-lg"
              >
                <a href={`/downloads/Madelmold.apk?v=${APP_CONFIG.version}`} download="Madelmold.apk">
                  <Download className="w-4 h-4" />
                  Descargar APK
                </a>
              </Button>
            </div>
          </div>

          {/* Install App Banner - PWA para iOS/Desktop */}
          {(isInstallable || isMobile) && !isInstalled && (
            <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-magma flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-foreground">Versión Web (PWA)</p>
                    <p className="text-sm text-muted-foreground">Para iOS o escritorio</p>
                  </div>
                </div>
                <Button
                  onClick={handleInstallClick}
                  variant="outline"
                  className="border-primary/30 hover:bg-primary/10 gap-2"
                >
                  <Download className="w-4 h-4" />
                  Instalar PWA
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
          {stats.map((stat, index) => (
            <div key={index} className="glass-card p-4 sm:p-6 text-center hover:shadow-magma-hover transition-all">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-magma bg-clip-text text-transparent mb-1 sm:mb-2">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 bg-gradient-magma bg-clip-text text-transparent">
          Características Principales
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="glass-card p-6 sm:p-8 hover:shadow-magma-hover transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-magma rounded-xl flex items-center justify-center mb-4 shadow-magma">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-base sm:text-lg font-semibold mb-2 text-foreground">{feature.title}</h4>
                <p className="text-sm sm:text-base text-muted-foreground">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="glass-card max-w-3xl mx-auto text-center p-8 sm:p-12">
          <h3 className="text-2xl sm:text-3xl font-bold mb-4 bg-gradient-magma bg-clip-text text-transparent">
            ¿Listo para optimizar tu producción?
          </h3>
          <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8">
            Únete a las empresas que ya confían en Madelmold MES
          </p>
          <Button 
            size="lg"
            onClick={() => navigate("/auth")}
            className="w-full sm:w-auto bg-gradient-magma-light text-white hover:shadow-magma-hover transition-all text-lg px-8 py-6 rounded-full font-semibold"
          >
            Comenzar Gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="glass-card m-4 sm:m-6 mt-12">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {/* Magma Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src={madelmoldLogo}
                  alt="Manufacturas Madelmold S.A."
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h4 className="font-bold text-foreground">Madelmold MES</h4>
                  <p className="text-xs text-muted-foreground">by Manufacturas Madelmold S.A.</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Sistema de gestión de manufactura para composites y procesos industriales avanzados.
              </p>
            </div>

            {/* Developer Section - Try Company */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src={tryLogo}
                  alt="Try! Company"
                  className="h-10 w-auto object-contain"
                />
                <div>
                  <h4 className="font-semibold text-foreground">Desarrollado por</h4>
                  <p className="text-xs text-muted-foreground">Try! Company</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground italic">
                "Un aliado integral que te reta a evolucionar"
              </p>
              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/try-business-solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
                  title="LinkedIn"
                >
                  <Linkedin className="w-4 h-4 text-primary" />
                </a>
                <a
                  href="https://www.instagram.com/trycompany.eu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
                  title="Instagram"
                >
                  <Instagram className="w-4 h-4 text-primary" />
                </a>
                <a
                  href="https://www.youtube.com/@trycompanyeurope"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
                  title="YouTube"
                >
                  <Youtube className="w-4 h-4 text-primary" />
                </a>
                <a
                  href="https://wa.me/34633455646"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center hover:bg-green-500/20 transition-colors"
                  title="WhatsApp"
                >
                  <MessageCircle className="w-4 h-4 text-green-600" />
                </a>
              </div>
            </div>

            {/* Contact & Support Section */}
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Contacto y Soporte</h4>
              <div className="space-y-3 text-sm">
                <a
                  href="mailto:info@trycompany.eu"
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  info@trycompany.eu
                </a>
                <a
                  href="tel:+34633229356"
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  +34 633 22 93 56
                </a>
                <a
                  href="https://wa.me/34633455646"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-muted-foreground hover:text-green-600 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp: +34 633 45 56 46
                </a>
                <a
                  href="https://www.trycompany.eu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  www.trycompany.eu
                </a>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  Calanda, Spain
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 border-t border-border/50">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
              <p>© {new Date().getFullYear()} Manufacturas Madelmold S.A.. Todos los derechos reservados.</p>
              <p className="flex items-center gap-1">
                Desarrollado con <span className="text-red-500">♥</span> por
                <a
                  href="https://www.trycompany.eu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:underline ml-1"
                >
                  Try! Company
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* iOS Install Instructions Dialog */}
      <Dialog open={showIOSDialog} onOpenChange={setShowIOSDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Instalar en iPhone/iPad
            </DialogTitle>
            <DialogDescription>
              Sigue estos pasos para instalar la aplicación en tu dispositivo iOS
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <p className="font-medium">Abre el menú de compartir</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Toca el botón <Share className="w-4 h-4 inline-block mx-1" /> en la barra de Safari
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <p className="font-medium">Desliza hacia abajo</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Busca la opción "Agregar a pantalla de inicio"
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <p className="font-medium">Agregar a pantalla de inicio</p>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                  Toca <PlusSquare className="w-4 h-4 inline-block" /> "Agregar a pantalla de inicio" y confirma
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowIOSDialog(false)} className="bg-gradient-magma">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Android Install Instructions Dialog */}
      <Dialog open={showAndroidDialog} onOpenChange={setShowAndroidDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Instalar en Android
            </DialogTitle>
            <DialogDescription>
              Sigue estos pasos para instalar la aplicación en tu dispositivo Android
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <p className="font-medium">Abre el menú de Chrome</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Toca los tres puntos <strong>⋮</strong> en la esquina superior derecha
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <p className="font-medium">Busca la opción de instalación</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Selecciona <strong>"Instalar aplicación"</strong> o <strong>"Agregar a pantalla de inicio"</strong>
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <p className="font-medium">Confirma la instalación</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Toca <strong>"Instalar"</strong> o <strong>"Agregar"</strong> para confirmar
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowAndroidDialog(false)} className="bg-gradient-magma">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
